// Privacy Policy, EN version

const privacyPolicyEn = `# Privacy Policy

*Last updated: May 13, 2026*

---

## Preamble

This Privacy Policy describes how **BPM**, a Simplified Joint-Stock Company (Société par Actions Simplifiée) under French law, with a share capital of €1,000, registered with the Trade and Companies Register (RCS) of NANTERRE under number 987 605 567, whose registered office is located at 73, Rue du Château, 92100 BOULOGNE-BILLANCOURT (hereinafter "BPM", "we" or "our"), collects, uses, shares, and protects the personal data of users (hereinafter "you" or "the user") of the website **beyondpersonalmatrix.com** and the service **Beyond Personal Matrix** (hereinafter "the Service").

BPM is committed to complying with the General Data Protection Regulation (EU) 2016/679 ("GDPR") and the French Data Protection Act n° 78-17 of January 6, 1978, as amended.

---

## 1. Data Controller

The data controller is:

**BPM**
73, Rue du Château, 92100 BOULOGNE-BILLANCOURT (France)
SIREN: 987 605 567
Email: privacy@beyondpersonalmatrix.com

BPM has not appointed a Data Protection Officer (DPO) to date, as its structure and activities do not make such appointment mandatory under Article 37 of the GDPR.

---

## 2. Personal Data Collected

As part of using the Service, we collect the following categories of data:

### 2.1 Data provided directly by the user

- **Identity**: first name, last name (if provided)
- **Contact details**: email address
- **Payment data**: processed exclusively by our provider Stripe (BPM does not store the credit card number or the security code)
- **Psychometric test responses**: answers provided to DISC, Big Five, MBTI, Enneagram, VIA Strengths, Schwartz Values questionnaires
- **Partner data (Duo pack only)**: email address of the invited partner

### 2.2 Data collected automatically

- **Technical data**: IP address, browser type, operating system, browsing language, time zone
- **Connection data**: dates and times of connection, session duration
- **Usage data**: pages visited, features used, interactions with the Service
- **Cookies**: see our Cookie Policy presented during your first visit

### 2.3 Data generated by the Service

- **Psychometric results**: scores, profiles, recommendations generated from your responses
- **AI analysis reports**: interpretations produced by our artificial intelligence models based on your results

---

## 3. Purposes and Legal Bases of Processing

| Purpose | Legal basis (GDPR) |
|---|---|
| Creation and management of your user account | Performance of the contract (art. 6.1.b) |
| Provision of the Service (tests, results, reports) | Performance of the contract (art. 6.1.b) |
| Payment processing | Performance of the contract (art. 6.1.b) |
| Sending transactional emails (activation, confirmation) | Performance of the contract (art. 6.1.b) |
| Billing and accounting obligations | Legal obligation (art. 6.1.c) |
| Customer support | Legitimate interest (art. 6.1.f) |
| Audience measurement and Service improvement | Legitimate interest (art. 6.1.f) or consent (art. 6.1.a) depending on cookies |
| Fraud prevention | Legitimate interest (art. 6.1.f) |
| Sending marketing communications | Consent (art. 6.1.a), revocable at any time |

---

## 4. Recipients and Subprocessors

Your personal data is accessible, within the limits of their respective duties, to:

- **Authorized BPM personnel**: for the performance of their duties
- **Technical subprocessors** listed below, strictly to the extent necessary for the execution of the Service

### List of Subprocessors

| Subprocessor | Purpose | Location | Safeguards |
|---|---|---|---|
| **Emergent** *(Emergent Methods, Inc.)* | Hosting of the Site and backend | United States | EU Commission Standard Contractual Clauses |
| **Stripe Payments Europe Ltd.** | Payment processing | Ireland (EU) + United States (transfers) | Standard Contractual Clauses + Data Processing Agreement |
| **Brevo** *(Sendinblue SAS)* | Sending transactional emails | France (EU) | EU subprocessor, GDPR-compliant by design |
| **MongoDB Atlas** | Database storage | EU *(to be confirmed depending on the chosen region)* | Standard Contractual Clauses + Data Processing Agreement |
| **PostHog** | Anonymized audience measurement | EU (eu.i.posthog.com) | EU subprocessor, GDPR-compliant by design |
| **Google Analytics 4** *(Google Ireland Ltd.)* | Audience measurement | Ireland (EU) + United States (transfers) | Standard Contractual Clauses + IP anonymization |
| **Google Workspace** *(Google Ireland Ltd.)* | Professional email support@ and noreply@ | Ireland (EU) + United States | Standard Contractual Clauses + Data Processing Agreement |
| **Namecheap, Inc.** | Domain name registration | United States | No access to Service users' personal data |

---

## 5. Transfers Outside the European Union

Some of our subprocessors may transfer or process your data in the United States (Emergent, Stripe for certain functions, Google Workspace for certain functions, Namecheap).

These transfers are governed by the **Standard Contractual Clauses** adopted by the European Commission (decision 2021/914), guaranteeing a level of data protection equivalent to that of the European Union.

---

## 6. Data Retention Periods

| Data category | Retention period |
|---|---|
| Account data (email, first name) | Duration of subscription, then 3 years after last activity (prospection) |
| Test responses | Duration of subscription, then 3 years for anonymized statistical analysis |
| Results and AI reports | Duration of subscription |
| Payment data | Not stored by BPM (Stripe retains them in accordance with its PCI-DSS obligations) |
| Invoices and accounting data | 10 years from the close of the financial year (Article L.123-22 of the French Commercial Code) |
| Technical logs | 12 months maximum |
| Cookies | According to user preferences, max. 13 months |

At the end of these periods, your data is deleted or anonymized.

---

## 7. Your Rights

In accordance with the GDPR, you have the following rights:

- **Right of access** (art. 15): obtain confirmation that data concerning you is being processed and obtain a copy
- **Right to rectification** (art. 16): correct inaccurate or incomplete data
- **Right to erasure** ("right to be forgotten", art. 17): delete your data in the cases provided for by the GDPR
- **Right to restriction of processing** (art. 18)
- **Right to portability** (art. 20): receive your data in a structured, machine-readable format
- **Right to object** (art. 21): object to processing for reasons relating to your particular situation, or at any time for commercial prospection
- **Right to withdraw your consent** at any time when processing is based on it
- **Right to give directives** concerning the fate of your data after your death (Article 85 of the French Data Protection Act)

### How to exercise your rights

To exercise these rights, you can contact us at: **privacy@beyondpersonalmatrix.com**.

We will respond within **one month** of receipt of your request (this period may be extended by two additional months for complex requests, in accordance with Article 12 of the GDPR).

Proof of identity may be requested in case of reasonable doubt about your identity.

### Complaint to a supervisory authority

If you consider, after contacting us, that your rights are not being respected, you may lodge a complaint with the **French Data Protection Authority (CNIL)**:
3 Place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07
Website: [www.cnil.fr](https://www.cnil.fr)

You may also lodge a complaint with the data protection authority of your habitual residence within the EU.

---

## 8. Security

BPM implements appropriate technical and organizational measures to protect your personal data against loss, alteration, disclosure, or unauthorized access. These measures include in particular:

- Encryption of communications via TLS (HTTPS)
- Password encryption (bcrypt hashing)
- Access to data limited to authorized persons
- Access monitoring and logging
- Regular database backups
- Regular security updates

As no system is completely infallible, BPM cannot guarantee absolute security. In the event of a data breach likely to result in a risk to your rights and freedoms, BPM undertakes to notify the CNIL within 72 hours in accordance with Article 33 of the GDPR, and to inform you as soon as possible in accordance with Article 34.

---

## 9. Cookies

The Site uses cookies and similar technologies. The management of these cookies is subject to a separate policy, accessible via the cookie management banner presented during your first visit. You can modify your preferences at any time.

In particular, the following are used:
- **Strictly necessary cookies** (authentication, cart, language): no consent required
- **Audience measurement cookies** (PostHog in GDPR mode eu.i.posthog.com, Google Analytics 4 with IP anonymization): subject to your consent
- **Third-party payment cookies** (Stripe): strictly necessary for payment finalization

---

## 10. Minors

The Service is not intended for minors under 15 years of age (digital majority threshold in France within the meaning of Article 8 of the GDPR). If you are under 15, you must obtain the consent of your parents or legal guardians before using the Service.

BPM reserves the right to request proof of age in case of reasonable doubt, and to delete the account of a user whose minority would be established without parental consent.

---

## 11. Modifications

BPM reserves the right to modify this Privacy Policy at any time. Any substantial modification will be notified to you by email and/or by an information banner on the Site, with reasonable notice before entry into force. Continued use of the Service after notification constitutes acceptance of the modified version.

The date of the last update appears at the top of this document.

---

## 12. Contact

For any question relating to this Privacy Policy or to the processing of your personal data:

**BPM**
73, Rue du Château, 92100 BOULOGNE-BILLANCOURT
Email: privacy@beyondpersonalmatrix.com
`;

export default privacyPolicyEn;
